import { type UseQueryResult, useQueries } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useDraftMode } from "../contexts/DraftModeContext";
import { DAYS_PER_WEEK, type ScheduleDay, type SchedulePerson, type ScheduleWeek } from "../utils/models";
import { daysInRange, peopleOf, toIsoDate, weekStartsBetween } from "../utils/scheduleWeeks";
import { prefetchScheduleWeek, scheduleWeekQuery } from "./scheduleCache";
import { usePrefetchNeighbourWeeks } from "./usePrefetchNeighbourWeeks";
import { useScheduleCacheEnabled, useScheduleStartDay } from "./useScheduleWeekRules";

export type ScheduleDays = {
	/** The days from `from` through `to` that have arrived, in date order; a week still loading is missing. */
	days: ScheduleDay[];
	/** Everyone in those weeks, once. */
	nurses: Record<number, SchedulePerson>;
	/** The first and last day the department has a schedule for: undefined until a week arrives, null with no schedule. */
	scheduleStartDate: DateTime | null | undefined;
	scheduleEndDate: DateTime | null | undefined;
	isLoading: boolean;
	/** Whether a week holding any day from `from` through `to`, within the days above, is still loading. */
	isLoadingBetween: (from: DateTime, to: DateTime) => boolean;
	isError: boolean;
	refetch: () => void;
};

/**
 * The schedule days from `from` through `to`, one cached week per request. Each week shows as soon as it
 * arrives, so a Month view fills in week by week. Once every week is in, the weeks just before and after are
 * prefetched while the browser is idle, never past the department's schedule.
 *
 * In draft mode the edits live in the cached weeks, so until the draft is published or discarded (both reload the
 * weeks from the server) a cached week never refetches on its own, isn't dropped from memory while the admin looks
 * at other weeks, and nothing is prefetched. React Query keeps the longer memory time on those weeks afterwards,
 * so the few weeks seen during a draft stay in memory until logout or a start-day change.
 *
 * A failure is tracked here but not shown: each screen shows `isError` in its own error state, so a page that
 * reads the schedule in two places doesn't stack two messages.
 */
export const useScheduleDays = (from: DateTime, to: DateTime): ScheduleDays => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;
	const enabled = useScheduleCacheEnabled();
	const startDay = useScheduleStartDay();
	const { trackError } = useAnalytics();
	const { isDraftMode } = useDraftMode();
	const fromDate = toIsoDate(from);
	const toDate = toIsoDate(to);

	const weekStarts = useMemo(() => weekStartsBetween(DateTime.fromISO(fromDate), DateTime.fromISO(toDate), startDay), [fromDate, toDate, startDay]);

	const combine = useCallback(
		(results: UseQueryResult<ScheduleWeek>[]): ScheduleDays => {
			const weeks = results.flatMap((result) => (result.data ? [result.data] : []));
			// Every response carries the same bounds, so the first week that arrived answers for all of them.
			const firstWeek = weeks.at(0);
			return {
				days: daysInRange(weeks, fromDate, toDate),
				nurses: peopleOf(weeks),
				scheduleStartDate: firstWeek?.scheduleStartDate,
				scheduleEndDate: firstWeek?.scheduleEndDate,
				isLoading: results.some((result) => result.isLoading),
				isLoadingBetween: (rangeFrom, rangeTo) => {
					const firstDay = toIsoDate(rangeFrom);
					const lastDay = toIsoDate(rangeTo);
					return results.some((result, index) => {
						const weekStart = weekStarts[index];
						const weekEnd = toIsoDate(DateTime.fromISO(weekStart).plus({ days: DAYS_PER_WEEK - 1 }));
						return result.isLoading && weekStart <= lastDay && weekEnd >= firstDay;
					});
				},
				isError: results.some((result) => result.isError),
				refetch: () => {
					for (const result of results) void result.refetch();
				},
			};
		},
		[fromDate, toDate, weekStarts],
	);

	const schedule = useQueries({
		queries: weekStarts.map((weekStart) => ({
			...scheduleWeekQuery(departmentId, weekStart),
			enabled,
			...(isDraftMode && { staleTime: Number.POSITIVE_INFINITY, gcTime: Number.POSITIVE_INFINITY }),
		})),
		combine,
	});

	useEffect(() => {
		if (schedule.isError) trackError("schedule_week_fetch_error", { department_id: departmentId, start_date: fromDate, end_date: toDate });
	}, [schedule.isError, departmentId, fromDate, toDate, trackError]);

	usePrefetchNeighbourWeeks(departmentId, weekStarts, enabled && !isDraftMode && !schedule.isLoading && !schedule.isError, prefetchScheduleWeek);

	return schedule;
};

/** One schedule day; undefined until its week arrives. */
export const useScheduleDay = (date: DateTime): Omit<ScheduleDays, "days"> & { day: ScheduleDay | undefined } => {
	const { days, ...schedule } = useScheduleDays(date, date);
	return { ...schedule, day: days.at(0) };
};
